import { useEffect, useRef, useState } from 'react';
import { CalendarBlank, CaretLeft, CaretRight } from 'shared/icons';
import Button from './Button';

// Parse "YYYY-MM-DD" em Date local (evita timezone shift de new Date('YYYY-MM-DD')).
const parseDateInput = (value) => {
    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
    }
    if (typeof value !== 'string' || !value) {
        return null;
    }
    const [y, m, d] = value.split('-').map((part) => Number.parseInt(part, 10));
    if (!y || !m || !d) {
        return null;
    }
    return new Date(y, m - 1, d);
};

/**
 * Componente controlado de filtro de intervalo de datas.
 *
 * Props:
 *   value   { from: 'YYYY-MM-DD', to: 'YYYY-MM-DD' } — estado controlado pelo pai.
 *   onApply (fromStr, toStr) — chamado APENAS quando o usuário aplica uma seleção.
 *                              Nunca auto-fira em mount.
 */
const DateRangeFilter = ({ value, onApply }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [startDate, setStartDate] = useState(() => parseDateInput(value?.from));
    const [endDate, setEndDate] = useState(() => parseDateInput(value?.to));
    const [hoverDate, setHoverDate] = useState(null);
    const [leftMonth, setLeftMonth] = useState(new Date());
    const [rightMonth, setRightMonth] = useState(new Date());
    const dropdownRef = useRef(null);

    // Sincroniza com value externo quando ele muda.
    useEffect(() => {
        setStartDate(parseDateInput(value?.from));
        setEndDate(parseDateInput(value?.to));
    }, [value?.from, value?.to]);

    // Configurar meses (esquerdo = atual, direito = proximo)
    useEffect(() => {
        const today = new Date();
        const nextMonth = new Date(today);
        nextMonth.setMonth(nextMonth.getMonth() + 1);

        setLeftMonth(today);
        setRightMonth(nextMonth);
    }, []);

    // Fechar dropdown ao clicar fora
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    // Formatar data para input (YYYY-MM-DD)
    const formatDateForInput = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Formatar data para exibicao (DD/MM/YYYY)
    const formatDateForDisplay = (date) => {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    // Obter o inicio do dia (meia-noite)
    const getStartOfDay = (date) => {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    };

    // Verificar se data esta no range
    const isInRange = (date) => {
        if (!startDate || !endDate) {
            return false;
        }
        const start = getStartOfDay(startDate);
        const end = getStartOfDay(endDate);
        const day = getStartOfDay(date);
        return day >= start && day <= end;
    };

    // Verificar se data esta no hover range
    const isInHoverRange = (date) => {
        if (!startDate || !hoverDate || endDate) {
            return false;
        }
        const min = startDate < hoverDate ? startDate : hoverDate;
        const max = startDate < hoverDate ? hoverDate : startDate;
        const start = getStartOfDay(min);
        const end = getStartOfDay(max);
        const day = getStartOfDay(date);
        return day >= start && day <= end;
    };

    // Handler de selecao de data
    const handleDateClick = (date) => {
        if (!startDate || (startDate && endDate)) {
            // Iniciar nova selecao
            setStartDate(date);
            setEndDate(null);
        } else {
            // Completar selecao
            let from;
            let to;
            if (date < startDate) {
                from = date;
                to = startDate;
            } else {
                from = startDate;
                to = date;
            }

            setStartDate(from);
            setEndDate(to);

            // Aplicar automaticamente apos selecionar segunda data
            applyDateRange(from, to);
        }
    };

    // Aplicar periodo e fechar dropdown
    const applyDateRange = (from, to) => {
        const fromStr = formatDateForInput(from);
        const toStr = formatDateForInput(to);

        onApply(fromStr, toStr);
        setIsOpen(false);
    };

    // Handler de preset rapido
    const handleQuickPreset = (days) => {
        const today = new Date();
        const pastDate = new Date();
        pastDate.setDate(today.getDate() - days);

        setStartDate(pastDate);
        setEndDate(today);
        applyDateRange(pastDate, today);
    };

    // Navegar entre meses
    const changeMonth = (side, direction) => {
        const month = side === 'left' ? new Date(leftMonth) : new Date(rightMonth);
        month.setMonth(month.getMonth() + direction);

        if (side === 'left') {
            setLeftMonth(month);
            // Garantir que o mes direito seja sempre posterior ao esquerdo
            if (month >= rightMonth) {
                const newRightMonth = new Date(month);
                newRightMonth.setMonth(newRightMonth.getMonth() + 1);
                setRightMonth(newRightMonth);
            }
        } else {
            setRightMonth(month);
            // Garantir que o mes esquerdo seja sempre anterior ao direito
            if (month <= leftMonth) {
                const newLeftMonth = new Date(month);
                newLeftMonth.setMonth(newLeftMonth.getMonth() - 1);
                setLeftMonth(newLeftMonth);
            }
        }
    };

    // Renderizar calendario
    const renderCalendar = (month, side) => {
        const monthNames = [
            'Janeiro',
            'Fevereiro',
            'Marco',
            'Abril',
            'Maio',
            'Junho',
            'Julho',
            'Agosto',
            'Setembro',
            'Outubro',
            'Novembro',
            'Dezembro',
        ];
        const weekdays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'];

        // Calcular primeiro dia do mes
        const firstDay = new Date(month.getFullYear(), month.getMonth(), 1);
        const startDate = new Date(firstDay);
        startDate.setDate(startDate.getDate() - firstDay.getDay());

        // Gerar dias
        const days = [];
        for (let i = 0; i < 42; i++) {
            const currentDate = new Date(startDate);
            currentDate.setDate(startDate.getDate() + i);
            days.push(currentDate);
        }

        return (
            <div className="flex-1 min-w-[280px]">
                {/* Header do mes */}
                <div className="flex items-center justify-between mb-3">
                    <button
                        type="button"
                        onClick={() => changeMonth(side, -1)}
                        className="inline-flex items-center justify-center p-2 text-mono-600 dark:text-mono-400 hover:text-black dark:hover:text-white hover:bg-mono-100 dark:hover:bg-mono-900 rounded-lg transition-colors pointer-coarse:min-h-11 pointer-coarse:min-w-11"
                    >
                        <CaretLeft size={16} weight="bold" />
                    </button>
                    <span className="text-sm font-semibold text-black dark:text-white">
                        {monthNames[month.getMonth()]} {month.getFullYear()}
                    </span>
                    <button
                        type="button"
                        onClick={() => changeMonth(side, 1)}
                        className="inline-flex items-center justify-center p-2 text-mono-600 dark:text-mono-400 hover:text-black dark:hover:text-white hover:bg-mono-100 dark:hover:bg-mono-900 rounded-lg transition-colors pointer-coarse:min-h-11 pointer-coarse:min-w-11"
                    >
                        <CaretRight size={16} weight="bold" />
                    </button>
                </div>

                {/* Grid de calendario */}
                <div className="grid grid-cols-7 gap-1">
                    {/* Dias da semana */}
                    {weekdays.map((day) => (
                        <div
                            key={day}
                            className="text-center text-xs font-semibold text-mono-600 dark:text-mono-400 py-2"
                        >
                            {day}
                        </div>
                    ))}

                    {/* Dias do mes */}
                    {days.map((date) => {
                        const isCurrentMonth = date.getMonth() === month.getMonth();
                        const inRange = isInRange(date);
                        const inHover = isInHoverRange(date);

                        return (
                            <button
                                key={formatDateForInput(date)}
                                type="button"
                                onClick={() => isCurrentMonth && handleDateClick(date)}
                                onMouseEnter={() => isCurrentMonth && setHoverDate(date)}
                                onMouseLeave={() => setHoverDate(null)}
                                disabled={!isCurrentMonth}
                                className={`
                  aspect-square flex items-center justify-center text-sm rounded-lg transition-colors text-black dark:text-white
                  ${!isCurrentMonth ? 'opacity-25' : ''}
                  ${inRange || inHover ? 'bg-info-soft dark:bg-info-950 text-info-600 dark:text-info-400' : ''}
                `}
                            >
                                {date.getDate()}
                            </button>
                        );
                    })}
                </div>
            </div>
        );
    };

    return (
        <div className="relative" ref={dropdownRef}>
            {/* Botao de trigger */}
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-3 px-4 py-2 text-sm font-medium text-black dark:text-white bg-white dark:bg-mono-950 border border-mono-200 dark:border-mono-800 rounded-lg hover:bg-mono-50 dark:hover:bg-mono-900 transition-colors"
            >
                <CalendarBlank size={20} weight="bold" />
                <span>
                    {startDate && endDate
                        ? `${formatDateForDisplay(startDate)} - ${formatDateForDisplay(endDate)}`
                        : 'Selecionar periodo'}
                </span>
            </button>

            {/* Dropdown */}
            {isOpen && (
                <div className="absolute top-full left-0 mt-2 bg-white dark:bg-mono-950 border border-mono-200 dark:border-mono-800 rounded-xl shadow-2xl z-50 overflow-hidden">
                    {/* Presets rapidos */}
                    <div className="p-4 border-b border-mono-200 dark:border-mono-800 bg-mono-50 dark:bg-mono-900">
                        <p className="text-xs font-semibold text-mono-600 dark:text-mono-400 uppercase tracking-wider mb-3">
                            Periodos rapidos
                        </p>
                        <div className="flex flex-wrap gap-2">
                            <Button variant="secondary" size="sm" onClick={() => handleQuickPreset(7)}>
                                7 dias
                            </Button>
                            <Button variant="secondary" size="sm" onClick={() => handleQuickPreset(15)}>
                                15 dias
                            </Button>
                            <Button variant="secondary" size="sm" onClick={() => handleQuickPreset(30)}>
                                30 dias
                            </Button>
                            <Button variant="secondary" size="sm" onClick={() => handleQuickPreset(60)}>
                                60 dias
                            </Button>
                            <Button variant="secondary" size="sm" onClick={() => handleQuickPreset(90)}>
                                90 dias
                            </Button>
                            <Button variant="secondary" size="sm" onClick={() => handleQuickPreset(180)}>
                                6 meses
                            </Button>
                            <Button variant="secondary" size="sm" onClick={() => handleQuickPreset(365)}>
                                12 meses
                            </Button>
                        </div>
                    </div>

                    {/* Calendarios lado a lado */}
                    <div className="flex gap-6 p-6">
                        {renderCalendar(leftMonth, 'left')}
                        {renderCalendar(rightMonth, 'right')}
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between px-6 py-4 border-t border-mono-200 dark:border-mono-800 bg-mono-50 dark:bg-mono-900">
                        <p className="text-xs text-mono-600 dark:text-mono-400">
                            {startDate && !endDate && 'Selecione a data final'}
                            {startDate &&
                                endDate &&
                                `${formatDateForDisplay(startDate)} - ${formatDateForDisplay(endDate)}`}
                        </p>
                        <button
                            type="button"
                            onClick={() => {
                                setStartDate(null);
                                setEndDate(null);
                                setIsOpen(false);
                            }}
                            className="px-3 py-1.5 text-xs font-medium text-mono-700 dark:text-mono-300 hover:text-black dark:hover:text-white hover:bg-mono-100 dark:hover:bg-mono-800 rounded-lg transition-colors"
                        >
                            Limpar
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DateRangeFilter;
